import { Mail, Lock } from 'lucide-react'
import { Layout } from '@/components/layout'

export function PrivacyPolicyPage() {
  return (
    <Layout>
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-6 py-12">
          <h1 className="text-4xl font-bold tracking-tight text-foreground mb-3">Privacybeleid</h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            BGCH Crisis Communication Buddy
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-3xl space-y-8">
          {/* Section 1: Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              1. Inleiding
            </h2>
            <p className="text-foreground mb-4">
              De BGCH Crisiscommunicatie Buddy (hierna "Crisisbuddy") is een AI-aangedreven webtoepassing
              die ontworpen is om Nederlandse gemeentelijke crisiscommunicatieteams te ondersteunen. Wij nemen uw privacy
              serieus en zijn toegewijd aan het beschermen van uw persoonlijke gegevens in overeenstemming met de Algemene Verordening Gegevensbescherming (GDPR).
            </p>
            <p className="text-foreground">
              Dit privacybeleid beschrijft hoe we uw persoonlijke gegevens verzamelen, gebruiken, opslaan en beschermen.
            </p>
          </section>

          {/* Section 2: Scope */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              2. Werkingsgebied
            </h2>
            <p className="text-foreground mb-3">Dit privacybeleid is van toepassing op:</p>
            <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
              <li>De hoofdapplicatie Crisisbuddy op <strong>crisisbuddy.nl</strong> en <strong>www.crisisbuddy.nl</strong></li>
              <li>Alle bijbehorende API's en backend-services</li>
              <li>Integraties met derden (Mistral AI, Coosto, SharePoint) voor zover deze betrekking hebben op Crisisbuddy</li>
            </ul>
            <p className="text-foreground mt-4">
              Dit beleid is niet van toepassing op websites of services van derden die via links uit Crisisbuddy bereikbaar zijn.
              Wij zijn niet verantwoordelijk voor de privacybeleid van derde partijen.
            </p>
          </section>

          {/* Section 3: Data Collection */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              3. Verzameling van gegevens
            </h2>
            <p className="text-foreground mb-4">We verzamelen de volgende categorieën persoonlijke gegevens:</p>

            <div className="space-y-4 ml-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">3.1 Accountgegevens</h3>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>Volledige naam</li>
                  <li>E-mailadres</li>
                  <li>Gebruikersnaam en wachtwoord (versleuteld)</li>
                  <li>Organisatie en functietitel</li>
                  <li>Telefoonnummer (optioneel)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">3.2 Crisisbeheergegevens</h3>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>Crisistype en locatie</li>
                  <li>Crisisbeschrijving en context</li>
                  <li>Gegevens over contacten en partners</li>
                  <li>Gescande mediacontent en nieuwsberichten</li>
                  <li>AI-gegenereerde communicatieconcepten</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">3.3 Technische gegevens</h3>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>IP-adres en apparaattype</li>
                  <li>Browsertype en versie</li>
                  <li>Besturingssysteem</li>
                  <li>Navigatiegegevens en toegetslogboeken</li>
                  <li>Gebruiksstatistieken en prestatiemetriek</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">3.4 Communicatiegegevens</h3>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>Ondersteunend- en feedback-e-mails</li>
                  <li>Probleemrapportages en suggesties</li>
                  <li>Communicatie met ons ondersteuningsteam</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4: Data Usage */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              4. Gebruik van gegevens
            </h2>
            <p className="text-foreground mb-4">We gebruiken uw gegevens voor de volgende doeleinden:</p>
            <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
              <li><strong>Servicelevering:</strong> Het verstrekken en onderhouden van Crisisbuddy</li>
              <li><strong>Authenticatie:</strong> Het verifiëren van uw identiteit en toegangsbeheer</li>
              <li><strong>AI-assistentie:</strong> Verwerking door Mistral AI voor inhoudsgeneratie en analyse</li>
              <li><strong>Auditsporen:</strong> Het bijhouden van alle acties voor naleving en veiligheid</li>
              <li><strong>Communicatie:</strong> Het sturen van updates, waarschuwingen en ondersteuningsberichten</li>
              <li><strong>Analyse:</strong> Het begrijpen van hoe u Crisisbuddy gebruikt om het te verbeteren</li>
              <li><strong>Wettelijke naleving:</strong> Het voldoen aan juridische verplichtingen</li>
              <li><strong>Beveiligingsmonitoring:</strong> Het detecteren en voorkomen van misbruik</li>
            </ul>
          </section>

          {/* Section 5: Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              5. Gegevensbehoud
            </h2>
            <p className="text-foreground mb-4">
              We behouden uw persoonlijke gegevens zolang nodig is voor de doeleinden waarvoor deze zijn verzameld,
              behalve wanneer wettelijk langer bewaren vereist is:
            </p>
            <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
              <li><strong>Accountgegevens:</strong> Zolang uw account actief is, plus 12 maanden na verwijdering</li>
              <li><strong>Crisisbeheergegevens:</strong> Voor de volledige levensduur van de crisis en 5 jaar daarna voor audit</li>
              <li><strong>Technische logboeken:</strong> 90 dagen voor normale activiteiten, langer voor beveiligingslogs</li>
              <li><strong>Communicatiegegevens:</strong> 2 jaar voor ondersteuningsinhoud, langer indien nodig voor geschillen</li>
            </ul>
          </section>

          {/* Section 6: Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              6. Uw rechten
            </h2>
            <p className="text-foreground mb-4">
              In overeenstemming met de GDPR hebt u de volgende rechten met betrekking tot uw persoonlijke gegevens:
            </p>

            <div className="space-y-4 ml-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">6.1 Recht op toegang</h3>
                <p className="text-foreground">
                  U kunt verzoeken om een kopie van alle persoonlijke gegevens die we over u hebben.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">6.2 Recht op rectificatie</h3>
                <p className="text-foreground">
                  U kunt ons vragen om onjuiste of onvolledige gegevens te corrigeren.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">6.3 Recht op verwijdering</h3>
                <p className="text-foreground">
                  U kunt verzoeken om uw gegevens te verwijderen, tenzij we deze legaal moeten bewaren.
                  Dit wordt ook wel het "recht om vergeten te worden" genoemd.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">6.4 Recht op beperking van verwerking</h3>
                <p className="text-foreground">
                  U kunt verzoeken om te beperken hoe we uw gegevens gebruiken terwijl we een geschil behandelen.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">6.5 Recht op gegevensportabiliteit</h3>
                <p className="text-foreground">
                  U kunt uw gegevens in een gestructureerd, gangbaar formaat aanvragen voor overdracht naar een ander bedrijf.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">6.6 Recht op bezwaar</h3>
                <p className="text-foreground">
                  U kunt bezwaar maken tegen bepaalde verwerkingen van uw gegevens, met name voor marketingdoeleinden.
                </p>
              </div>
            </div>

            <p className="text-foreground mt-4">
              Om deze rechten uit te oefenen, contacteer ons onder de contactgegevens in sectie 10.
            </p>
          </section>

          {/* Section 7: Third-Party Services */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              7. Diensten van derden
            </h2>
            <p className="text-foreground mb-4">
              Crisisbuddy integreert met de volgende derdepartijenservices. Uw gegevens kunnen naar deze partijen worden verzonden
              voor het leveren van bepaalde functies:
            </p>

            <div className="space-y-4 ml-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">7.1 Mistral AI</h3>
                <p className="text-foreground mb-1">
                  <strong>Doel:</strong> AI-aangedreven inhoudsgeneratie en tekstanalyse
                </p>
                <p className="text-foreground mb-1">
                  <strong>Gegevens gedeeld:</strong> Crisisbeschrijvingen, mediacontent (zonder PII)
                </p>
                <p className="text-foreground text-sm">
                  <strong>Privacy:</strong> Mistral AI verwerkt gegevens in overeenstemming met hun privacybeleid.
                  Geen persoonlijk identificeerbare informatie wordt doorgegeven.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">7.2 Coosto</h3>
                <p className="text-foreground mb-1">
                  <strong>Doel:</strong> Media monitoring en sociale mediaanalyse
                </p>
                <p className="text-foreground mb-1">
                  <strong>Gegevens gedeeld:</strong> Zoekopdrachten en monitoringparameters (gerelateerd aan crisistype en locatie)
                </p>
                <p className="text-foreground text-sm">
                  <strong>Privacy:</strong> Coosto verwerkt openbare mediadata in overeenstemming met hun privacybeleid.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">7.3 Microsoft (SharePoint)</h3>
                <p className="text-foreground mb-1">
                  <strong>Doel:</strong> Optioneel: Documentbeheer en samenwerkingsfuncties
                </p>
                <p className="text-foreground mb-1">
                  <strong>Gegevens gedeeld:</strong> Crisisdocumenten en samenwerkingsgegevens (alleen indien ingeschakeld)
                </p>
                <p className="text-foreground text-sm">
                  <strong>Privacy:</strong> Microsoft verwerkt gegevens in overeenstemming met hun privacy statement.
                </p>
              </div>
            </div>

            <p className="text-foreground mt-4">
              We hebben verwerkingsovereenkomsten gesloten met alle derde partijen om ervoor te zorgen dat uw gegevens op dezelfde
              manier beschermd worden als in Crisisbuddy.
            </p>
          </section>

          {/* Section 8: Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              8. Gegevensbeveiliging
            </h2>
            <p className="text-foreground mb-4">
              We implementeren meerdere beveiligingsmaatregelen om uw gegevens tegen verlies, misbruik en ongeautoriseerde toegang te beschermen:
            </p>

            <div className="space-y-4 ml-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Versleuteling
                </h3>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>HTTPS/TLS-versleuteling voor alle gegevenstransmissie</li>
                  <li>Versleuteling van gevoelige gegevens in opslag</li>
                  <li>Veilige wachtwoordopslag met moderne hashing-algoritmen</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Toegangscontrole</h3>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>Op rollen gebaseerde toegangscontrole (RBAC)</li>
                  <li>JWT-gebaseerde authenticatie</li>
                  <li>Sessiebeheer en timeout-instellingen</li>
                  <li>Auditlogboeken voor alle databewerkingen</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Infrastructuurbeveiliging</h3>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>Firewalls en netwerksegmentatie</li>
                  <li>Regelmatige beveiligingsaudits en penetratietests</li>
                  <li>Snelle verbetering van kwetsbaarheden</li>
                  <li>Monitoring en logboekregistratie van beveiligingsgebeurtenissen</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 9: Cookies and Tracking */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              9. Cookies en tracking
            </h2>
            <p className="text-foreground mb-4">
              Crisisbuddy gebruikt cookies voor authentificatie en sessiebeheer. We gebruiken <strong>geen</strong> cookies
              voor tracking of reclame.
            </p>

            <div className="space-y-4 ml-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">9.1 Essentiële cookies</h3>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li><strong>Sessiecookie:</strong> Vereist voor inloggen</li>
                  <li><strong>CSRF-beveiliging:</strong> Beschermt tegen aanvallen met vervalste verzoeken</li>
                  <li><strong>Taalvoorkeur:</strong> Onthoudt uw taalinstelling</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">9.2 Analytics</h3>
                <p className="text-foreground">
                  We kunnen anonieme prestatiemetriek (laden, fouten) verzamelen om de dienst te verbeteren.
                  Dit wordt niet gekoppeld aan uw identiteit.
                </p>
              </div>
            </div>
          </section>

          {/* Section 10: Contact */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              10. Contact
            </h2>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Privacyverantwoordelijke
              </h3>
              <div className="space-y-2 ml-4">
                <p className="text-foreground">
                  Als u vragen hebt over dit privacybeleid of uw persoonlijke gegevens, neem dan contact met ons op:
                </p>
                <p className="text-foreground">
                  <strong>E-mailadres:</strong>{' '}
                  <a href="mailto:privacy@techletes.ai" className="text-primary hover:underline">
                    privacy@techletes.ai
                  </a>
                </p>
                <p className="text-foreground">
                  <strong>Reactietijd:</strong> Binnen 10 werkdagen op privacyverzoeken
                </p>
                <p className="text-foreground mt-4">
                  Als u niet tevreden bent met onze respons, hebt u het recht om een klacht in te dienen bij uw lokale
                  gegevensbeschermingsautoriteit.
                </p>
              </div>
            </div>
          </section>

          {/* Section 11: Policy Changes */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              11. Wijzigingen in dit beleid
            </h2>
            <p className="text-foreground">
              We kunnen dit privacybeleid op elk moment wijzigen. Significante wijzigingen zullen via e-mail of een opvallende
              kennisgeving op Crisisbuddy worden aangekondigd. Uw voortgezet gebruik van Crisisbuddy na dergelijke wijzigingen
              betekent dat u het herziene beleid aanvaardt.
            </p>
          </section>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground">
              <strong>Laatst bijgewerkt:</strong> 3 december 2025
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Dit privacybeleid kan op elk moment worden bijgewerkt. Controleer periodiek op wijzigingen.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  )
}
